import {
  Block,
  Button,
  List,
  ListInput,
  ListItem,
  Page,
  Popup,
  Progressbar,
} from "framework7-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PopupNavbar from "../components/popup-navbar";
import { usePopupBackClose } from "../hooks/use-back-close";
import { useEnterKeys } from "../hooks/use-enter-keys";
import { vibrate } from "../hooks/use-haptics";
import { useToast } from "../hooks/use-toast";
import {
  renameParticipant,
  selectCurrentGroup,
} from "../redux/reducer/app-slice";
import {
  duplicateNameMessage,
  isDuplicateName,
  nameKey,
} from "../utils/participant-names";

const NAME_INPUT = "rename-participant-input";

const RenameParticipantPopup = ({ opened, participant, onClose }) => {
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const popupRef = useRef(null);
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const { showError } = useToast();
  const group = useSelector(selectCurrentGroup);
  const groupId = group?.id;
  const otherNames = (group?.participants ?? [])
    .filter((other) => `${other.id}` !== `${participant?.id}`)
    .map((other) => other.name);
  const backClose = usePopupBackClose(opened, () => !isSaving);
  useEnterKeys(backClose.id, { [NAME_INPUT]: { hint: "go" } });

  const currentName = participant?.name ?? "";
  useEffect(() => {
    if (opened) setName(currentName);
  }, [opened, currentName]);

  const isDuplicate =
    nameKey(name) !== nameKey(currentName) && isDuplicateName(name, otherNames);

  const closePopup = () => popupRef.current?.close();

  const save = (event) => {
    event?.preventDefault();
    if (isSaving || isDuplicate) return;
    const trimmed = name.trim();
    if (!trimmed) {
      showError("Bitte gib einen Namen ein.");
      return;
    }
    if (trimmed === currentName) {
      closePopup();
      return;
    }
    setIsSaving(true);
    dispatch(
      renameParticipant(
        groupId,
        participant.id,
        trimmed,
        () => {
          vibrate();
          setIsSaving(false);
          closePopup();
        },
        (message) => {
          setIsSaving(false);
          showError(message);
        },
      ),
    );
  };

  return (
    <Popup
      {...backClose}
      className="rename-participant-popup"
      opened={opened}
      onPopupOpened={(popup) => {
        popupRef.current = popup;
        inputRef.current?.querySelector("input")?.focus();
      }}
      onPopupClosed={() => {
        setIsSaving(false);
        onClose();
      }}
    >
      <Page className={isSaving ? "is-busy" : ""}>
        <PopupNavbar title="Person umbenennen" />
        <div className="popup-progress">
          {isSaving && <Progressbar infinite />}
        </div>
        <Block>
          <div ref={inputRef}>
            <List strong form formStore={false} onSubmit={save}>
              <ListInput
                name={NAME_INPUT}
                outline
                type="text"
                label="Name"
                floatingLabel
                placeholder="Name"
                autocomplete="off"
                autocapitalize="words"
                value={name}
                onChange={(event) => setName(event.target.value)}
                errorMessage={
                  isDuplicate ? duplicateNameMessage(name) : undefined
                }
                errorMessageForce={isDuplicate}
              />
              <ListItem>
                <Button
                  fill
                  className="rename-participant-save"
                  disabled={isSaving || isDuplicate}
                  onClick={save}
                >
                  Speichern
                </Button>
              </ListItem>
            </List>
          </div>
        </Block>
      </Page>
    </Popup>
  );
};

export default RenameParticipantPopup;
